import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { reportStepsAPI } from '../../services/api';
import './SheetPreviewModal.css';

interface SheetPreviewModalProps {
  taskId: string;
  sheetName: string;
  onClose: () => void;
}

const PAGE_SIZE = 50;

const SheetPreviewModal: React.FC<SheetPreviewModalProps> = ({ taskId, sheetName, onClose }) => {
  const { t } = useTranslation('create');
  const [columns, setColumns] = useState<string[]>([]);
  const [data, setData] = useState<any[][]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async (newOffset: number) => {
    setLoading(true);
    setError(null);
    try {
      const response = await reportStepsAPI.getSheetPreview(taskId, sheetName, PAGE_SIZE, newOffset);
      const result = response.data;
      setColumns(result.columns);
      setData(result.data);
      setTotalRows(result.total_rows);
      setOffset(newOffset);
    } catch (err: any) {
      setError(err.response?.data?.detail || t('sheet_preview.error_loading'));
    } finally {
      setLoading(false);
    }
  }, [taskId, sheetName]);

  useEffect(() => {
    fetchData(0);
  }, [fetchData]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const totalPages = Math.ceil(totalRows / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className="sheet-preview-overlay" onClick={onClose}>
      <div className="sheet-preview-modal" onClick={e => e.stopPropagation()}>
        <div className="sheet-preview-header">
          <div className="sheet-preview-title">
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
              <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 9H21" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9 21V9" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <h3>{sheetName}</h3>
            <span className="sheet-preview-rows-badge">{t('sheet_preview.rows_count', { count: totalRows })}</span>
          </div>
          <button className="sheet-preview-close" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="20" height="20">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>

        <div className="sheet-preview-body">
          {loading && (
            <div className="sheet-preview-loading">
              <div className="spinner"></div>
              <span>{t('sheet_preview.loading')}</span>
            </div>
          )}

          {error && (
            <div className="sheet-preview-error">{error}</div>
          )}

          {!loading && !error && (
            <div className="sheet-preview-table-wrapper">
              <table className="sheet-preview-table">
                <thead>
                  <tr>
                    <th className="row-number-col">#</th>
                    {columns.map((col, i) => (
                      <th key={i}>{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {data.map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      <td className="row-number-col">{offset + rowIdx + 1}</td>
                      {row.map((cell, cellIdx) => (
                        <td key={cellIdx}>{cell !== null && cell !== undefined ? String(cell) : ''}</td>
                      ))}
                    </tr>
                  ))}
                  {data.length === 0 && (
                    <tr>
                      <td colSpan={columns.length + 1} className="sheet-preview-empty">
                        {t('sheet_preview.no_data')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {totalPages > 1 && !loading && !error && (
          <div className="sheet-preview-pagination">
            <button
              disabled={offset === 0}
              onClick={() => fetchData(Math.max(0, offset - PAGE_SIZE))}
            >
              {t('sheet_preview.prev')}
            </button>
            <span>{t('sheet_preview.page_info', { current: currentPage, total: totalPages })}</span>
            <button
              disabled={offset + PAGE_SIZE >= totalRows}
              onClick={() => fetchData(offset + PAGE_SIZE)}
            >
              {t('sheet_preview.next')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SheetPreviewModal;
